import React, { useContext, useEffect, useRef, useState } from "react";
import ContextMenu from "../../NotWorkArea(Side&Head)/ContextMenu";
import { GridLoader } from "../../rcsgrid/createGridTools";
import { DocFieldType } from "../../Tools/Consts";
import { SmetaModalsButtonsContext } from "../../Tools/modalManager";
import { EditStyle, Prefixes } from "../../Tools/ProjectX";
import { getObjText, setParamObjectText } from "../../Tools/Requests";
import {
  defaultFieldInfo,
  getDateOfInt,
  getDefaultMenu,
  getIntOfStrDate,
  getStrDateOfDateObj,
} from "../../Tools/Tools";
import { getParamColumnsOptions } from "../../rcsgrid/GridTools/parseColumnTools";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";
import { createGrid, createRecordSource } from "../../rcsgrid/rcsgrid";
import smetaAxios from "../../../SmetaAxios";
import { HTMLGridPanelElement } from "../../../ComponentInterface";

/**
 * @param {Object} props
 * @param {string} [props.Module]
 * @param {string | number} [props.DataType]
 * @param {string | number} [props.EditStyle]
 * @param {string | number} [props.Options]
 * @param {Map} props.paramsRequest
 * @param {Function} [props.onSelect]
 */

interface DialogHistoryProps {
  Module: Prefixes;
  DataType: string;
  EditStyle: string | number;
  Options: string;
  paramsRequest: Map<string, string>;
  hiddenButton: boolean;
  Buttons: string;
  onSelect: (json: any) => void;
  noEdit?: boolean;
}

interface RecordData {
  UsedDate: string;
  ID: string;
  ObjRef?: string;
  Text?: string;
  EditVal?: string;
}

interface ParsedRecordData {
  UsedDate: { text: string; id: string };
  ID?: string;
  CheckField?: { text: string; id: string };
  Text?: string;
  EditVal?: string;
  ObjRef?: string;
}

interface HistoryData {
  Items?: { items?: RecordData[] };
  UsedDate?: string;
  Values?: { text: string; id: string }[];
}

/**
 * Возвращает данные для списка
 *
 * @param module модуль объекта
 * @param dataType тип объекта
 * @returns Возвращает объект со списком
 */
const getObjectValues = (module: Prefixes, dataType: string): { Items?: { text: string; id: string }[] } => {
  const params = {
    ObjType: dataType,
  };
  const config = {
    prefix: module,
    method: "get",
    url: "GetObjectValues",
    params,
  };

  return smetaAxios(config);
};

/**
 * Возвращает данные для таблицы
 *
 * @param paramsRequest данные параметра от родительского модуля
 * @returns Возвращает данные для таблицы истории
 */
const getParamHistory = (paramsRequest: Map<string, any>): Promise<HistoryData> => {
  const params = paramsRequest.entries().reduce((acc: { [key: string]: any }, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});

  const config = {
    prefix: params.prefix,
    method: "GET",
    url: "GetParamHistory",
    params,
  };
  delete params.prefix;
  return smetaAxios(config);
};

/**
 * Устанавливает новое значение для родительского параметра
 * @param paramsRequest данные параметра от родительского модуля
 * @param records массив данных записей с изменениями
 * @returns Возвращает данные с новым значением для параметра
 */
const setParamHistory = async (paramsRequest: Map<string, any>, records: ParsedRecordData[]) => {
  const params = paramsRequest.entries().reduce((acc: { [key: string]: any }, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});

  const data = records.map((element) => {
    return { UsedDate: element.UsedDate.id, ObjRef: element.ObjRef };
  });

  const config = {
    prefix: params.prefix,
    method: "POST",
    url: "SetParamHistory",
    data: { Items: data },
    params,
  };
  delete params.prefix;
  return smetaAxios(config);
};

/**
 * Контент диалога с историей
 *
 * Поля компонента:
 *    1. [mainGrid, setMainGrid] - управление состоянием таблицы
 *    2. gridpanelRef - ссылка на таблицу
 *    3. records - массив записей таблицы
 *    4. currentDay - текущая дата
 *    5. valueList - список значений
 *
 * @param props объект с данными для компонента диалога истории
 * @param props.Module модуль родительского объекта
 * @param props.DataType тип родительского объекта
 * @param props.EditStyle тип редактрования
 * @param props.Options опции для колонок таблицы
 * @param props.paramsRequest данные о параметре родительского модуля
 * @param props.onSelect функция установки нового значения
 * @returns Возвращает контент для диалога с историей
 */
export default function DialogHistory(props: DialogHistoryProps) {
  const {
    DataType: dataType,
    EditStyle: editStyle,
    Module: module,
    Options: options,
    Buttons: buttons,
    hiddenButton,
    paramsRequest,
    onSelect,
    noEdit,
  } = props;
  const [mainGrid, setMainGrid] = useState<{ grid: createGrid; source: createRecordSource }>();
  const gridPanelRef = useRef<HTMLGridPanelElement>(null);
  const records = useRef<ParsedRecordData[]>([]);
  const currentDay = useRef<{ text: string; id: string }>(undefined);
  const valueList = useRef<{ label: string; id?: string }[]>([]);
  const setButtons = useContext(SmetaModalsButtonsContext);

  /**
   * Инициализация таблицы
   */
  const initGrid = async () => {
    const res = await getParamHistory(paramsRequest);
    const resultRecords = getRecords(res);
    setCurrentDay(res);
    setListValues(res);
    records.current = resultRecords;
    mainGridLoader();
  };
  useEffect(() => {
    initGrid();
  }, []);

  useEffect(() => {
    if (hiddenButton) return;
    const modalButtons: { [key: number]: { label: string; onClick?: () => void; disabled?: boolean } } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = { label: "Установить", onClick: onSelectButton, disabled: noEdit };
    }
    setButtons?.(modalButtons);
  }, [setButtons, mainGrid, records, noEdit]);

  /**
   * Возвращает массив обработанных записей
   * @param data данные для таблицы истории
   * @returns Возвращает массив обработанных записей
   */
  const getRecords = (data: HistoryData): ParsedRecordData[] => {
    if (!data?.Items?.items?.length) return [];
    const items = data.Items.items;
    return items.map((item, index) => {
      const resultItem: ParsedRecordData = {
        ...item,
        UsedDate: {
          text: item.UsedDate ? getDateOfInt(item.UsedDate) : "",
          id: item.UsedDate,
        },
      };

      if (Number(editStyle) === EditStyle.Check) {
        resultItem.CheckField = { text: "", id: item.ObjRef || "0" };
        resultItem.Text = "";
        resultItem.EditVal = "";
      }
      return resultItem;
    });
  };

  /**
   * Устанавливает текущую дату из данных для таблицы истории или сегодняшнее число
   * @param data данные для таблицы истории
   */
  const setCurrentDay = (data: HistoryData) => {
    if (data.UsedDate) {
      currentDay.current = {
        text: getDateOfInt(data.UsedDate),
        id: data.UsedDate,
      };
    } else {
      const strDate = getStrDateOfDateObj(new Date());
      currentDay.current = { text: strDate, id: `${getIntOfStrDate(strDate)}` };
    }
  };

  /**
   * Устанавливает данные по умолчанию для списка в таблице
   * @param data данные для таблицы истории
   */
  const setListValues = (data: HistoryData) => {
    if (data.Values) {
      Object.values(data.Values).forEach((item) => {
        if (typeof item === "object") {
          valueList.current.push({ label: item.text, id: item.id });
        } else {
          valueList.current.push({ label: item });
        }
      });
    }
  };

  /**
   * Загружает локальную таблицу на основе данных о записях
   */
  const mainGridLoader = async () => {
    const res = await GridLoader(gridPanelRef.current, props, {
      localGrid: true,
      refresh: false,
      Columns: getColumns(),
      defaultFields: getDefaultFields(),
      keyField: "ID",
      recordKeys: ["ID", "UsedDate", "ObjRef", "Text", "EditVal", "CheckField"],
      titleKey: "Text",
      getFieldText: getFieldText,
      checkFieldText: checkFieldText,
      onPostRecord: onPostRecord,
      createRecord: createRecord,
      deleteRecord: deleteRecord,
      getRecords: () => records.current,
      Rights: noEdit ? "0" : "-1",
    });
    setMainGrid(res);
  };

  /**
   * Удаляет запись из таблицы
   * @param request запрос от таблицы
   * @returns Возвращает объект с данными для таблицы
   */
  const deleteRecord = (request: { RecordIndex: number }) => {
    const deletedID = gridPanelRef.current.grid.source.getFieldTextSync("ID", request.RecordIndex);
    const deletedIndex = records.current.findIndex((record) => record.ID === deletedID);
    if (deletedIndex !== -1) records.current.splice(deletedIndex, 1);
    return {
      Command: "DeleteRecord",
      RecordIndex: records.current.length === 0 ? 0 : request.RecordIndex - 1,
      RecordCount: records.current.length,
      UpdatesPending: "1",
      RecordKey: {},
    };
  };

  /**
   * Создает запись в таблице
   * @returns Возвращает объект с данными для таблицы
   */
  const createRecord = () => {
    const index = records.current.length ? gridPanelRef.current.grid.source.activeRecord + 1 : 0;
    const newRecords = [...records.current];
    if (currentDay.current) newRecords.splice(index, 0, { UsedDate: currentDay.current });
    return {
      index: index,
      records: newRecords,
      keys: ["UsedDate"],
      titleKey: "Text",
    };
  };

  /**
   * Проверяет значение редактора при редактировании
   * @param request запрос от таблицы
   * @returns Возвращает объект с данными для таблицы
   */
  const checkFieldText = async (request: { ObjRef: string }) => {
    const val = await getObjText(module, dataType, request.ObjRef, "-1");
    return { Text: val.Text, ObjRef: request.ObjRef, EditVal: val.Text };
  };

  /**
   * Находит и возвращает свободный id для записи новых данных
   * @param records массив записей
   * @returns возвращает свободный id
   */
  const findFreeId = (records: ParsedRecordData[]) => {
    const ids = records.map((record) => parseInt(record.ID || "", 10)).filter((id) => !isNaN(id));
    if (ids.length === 0) {
      return "1";
    }
    for (let i = 1; ; i++) {
      if (!ids.includes(i)) {
        return i.toString();
      }
    }
  };

  // TODO: Переделать функцию после рефактора rcsgrid.js
  const onPostRecord = async (
    request: { NewRecord: string; RecordIndex: number; RecordSet: any },
    requestData: any[],
  ) => {
    let attributeIndex = 0;
    if (request.NewRecord === "1") {
      if (currentDay.current)
        records.current.splice(request.RecordIndex, 0, {
          ID: findFreeId(records.current),
          UsedDate: currentDay.current,
        });
      if (requestData.length > 3) {
        attributeIndex = requestData.length - 1;
      } else attributeIndex = 1;
    }
    const modifiedField = request.RecordSet.Attributes[attributeIndex],
      modifiedData = requestData[attributeIndex];
    const record = records.current[request.RecordIndex];

    if (modifiedField === "UsedDate") {
      const newUsedDate = modifiedData.id && {
        text: getDateOfInt(modifiedData.id),
        id: modifiedData.id,
      };
      record.UsedDate = newUsedDate || { text: "", id: "" };
      return;
    }

    if (modifiedField === "CheckField") {
      record.CheckField = modifiedData;
      record.ObjRef = modifiedData ? "1" : "";
      return;
    }

    let newText = "",
      newObjRef = "",
      newEditVal = "",
      json;
    if (editStyle == EditStyle.PickList && module === undefined) {
      newText = modifiedData.text;
      newEditVal = modifiedData.text;
      newObjRef = modifiedData.id;
    } else if (editStyle == EditStyle.Check) {
      newObjRef = modifiedData.id;
    } else if (modifiedData) {
      if (!modifiedData.id) {
        json = await setParamObjectText(module, dataType, record.ObjRef, modifiedData.text);
        newText = json.Text;
        newObjRef = json.ObjRef;
        newEditVal = json.EditVal || newText;
      } else {
        newObjRef = modifiedData.id;
        newText = (await getObjText(module, dataType, newObjRef, "-1")).Text;
        newEditVal = newText;
      }
    }
    record.ObjRef = newObjRef;
    record.Text = newText;
    record.EditVal = newEditVal;
  };

  /**
   * Возвращает список со значениями для редактора
   * @returns Возвращает список в виде {label,id}
   */
  const textDropDownList = async () => {
    if (module) {
      const json = getObjectValues(module, dataType);
      if (!json.Items) return [];
      const vals = Object.values(json.Items);
      return vals.map((item) => {
        return { label: item.text, id: item.id };
      });
    } else {
      return valueList.current;
    }
  };

  /**
   * Возвращает колонки с опциями для таблицы
   * @returns Возвращает колонки для таблицы
   */
  const getColumns = () => {
    return {
      title: [
        {
          fieldName: "UsedDate",
          title: "Дата",
          width: 100,
          fieldInfo: {
            ...defaultFieldInfo,
            DataType: DocFieldType.IntDate,
            EditStyle: EditStyle.Calendar,
          },
        },
        {
          fieldName: "Text",
          title: "Значение",
          width: 200,
          fieldInfo: {
            ...defaultFieldInfo,
            onDropDownList: textDropDownList,
            DataType: dataType,
            EditStyle: editStyle,
            options: getParamColumnsOptions(options || 0),
            Module: module,
            CheckField: "CheckField",
          },
        },
      ],
    };
  };

  /**
   * Возвращает колонки по умолчанию для таблицы
   * @returns Возвращает колонки по умолчанию для таблицы
   */
  const getDefaultFields = () => {
    return [
      {
        Name: "ID",
        Title: "ID",
        Width: "7",
      },
      {
        Name: "UsedDate",
        Title: "Дата",
        Width: "10",
      },
      {
        Name: "ObjRef",
        Title: "ObjRef",
        Width: "7",
      },
      {
        Name: "Text",
        Title: "Значение",
        Width: "20",
      },
      {
        Name: "EditVal",
        Title: "Наименование для редактора",
        Width: "30",
      },
      {
        Name: "CheckField",
        Title: "",
        Width: "30",
      },
    ];
  };

  /**
   * Возвращает значение для редактора
   * @param request запрос таблицы
   * @returns Возвращает объект с данными для таблицы
   */
  const getFieldText = (request: { FieldName: string; RecordIndex: number }) => {
    if (records.current.length) {
      const val = request.FieldName === "Text" ? "EditVal" : request.FieldName;
      if (gridPanelRef.current.grid.source.getState() === 3) {
        if (val === "EditVal") return { Text: "" };
        return { Text: currentDay.current?.text || "" };
      }
      const recVal: any = records.current[request.RecordIndex];
      return { Text: recVal[val]?.text || recVal[val] };
    }
    return { Text: "" };
  };

  /**
   * Устанавливает новое значение при выборе из диалога с историей
   */
  const onSelectButton = async () => {
    onSelect?.(await setParamHistory(paramsRequest, records.current));
  };

  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.fullDialogFlexContainer)}>
        <ContextMenu for="grid" Menu={getDefaultMenu("history")} refs={{ current: mainGrid }}>
          <div id={`rcgrid_panel`} ref={gridPanelRef} style={{ width: "100%", height: "100%", position: "relative" }} />
        </ContextMenu>
      </div>
    </div>
  );
}
